/**
 * "Happening now" / "Ended" for an event row.
 *
 * Airtable serialises an event's `Date` as Chicago wall-clock with a Z:
 * 10 AM on the 18th arrives as `2026-10-18T10:00:00.000Z`. Everything else in
 * the app reads it that way (UTC components — see useEventDay and the
 * morning/afternoon split in Templates/Events/page.vue). This badge used to
 * hand it to `new Date()` as a real instant, which in Chicago is 5 AM, so
 * every Sunday event read "Happening now" from 5 AM (Asana, festival Sunday).
 *
 * So all three times — start, end and now — are compared as Chicago
 * wall-clock, each held in a UTC timestamp. That also makes the badge right
 * on a phone set to another timezone.
 */
export type EventStatus = 'live' | 'ended' | '';

const FESTIVAL_TZ = 'America/Chicago';

/** `nowMs` as Chicago wall-clock, in the same Z-means-local frame as Airtable. */
export function chicagoWallClock(nowMs: number): number {
    // sv-SE formats as "2026-10-18 08:30:00", which parses back as ISO.
    const local = new Date(nowMs).toLocaleString('sv-SE', { timeZone: FESTIVAL_TZ, hour12: false });
    return Date.parse(`${local.replace(' ', 'T')}Z`);
}

/** Start and end in the wall-clock frame; the end is parsed off `Time`, e.g. "10 AM - 1 PM". */
export function eventRange(iso: unknown, time: unknown): { start: number; end: number } | null {
    if (!iso || !time) return null;
    const start = Date.parse(String(iso));
    if (Number.isNaN(start)) return null;

    const endPart = String(time).split('-')[1]?.trim();
    const match = endPart?.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
    if (!match) return null;

    let hours = parseInt(match[1]!, 10) % 12;
    if (/pm/i.test(match[3]!)) hours += 12;
    const end = new Date(start);
    end.setUTCHours(hours, match[2] ? parseInt(match[2], 10) : 0, 0, 0);
    return end.getTime() > start ? { start, end: end.getTime() } : null;
}

const dayOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * Only for events happening today, so the whole list doesn't read "Ended" the
 * rest of the year — which is what it would do against any previous year's data.
 */
export function eventStatus(iso: unknown, time: unknown, nowMs: number): EventStatus {
    const range = eventRange(iso, time);
    if (!range) return '';
    const now = chicagoWallClock(nowMs);
    if (dayOf(now) !== dayOf(range.start)) return '';
    if (now >= range.start && now <= range.end) return 'live';
    if (now > range.end) return 'ended';
    return '';
}
